'use client';

import { type Dispatch, type JSX, type ReactNode, useEffect, useReducer, useState } from 'react';

import {
  getHowItWorksStepDurationMs,
  HOW_IT_WORKS_SECTION_INTERSECTION_THRESHOLD,
  HOW_IT_WORKS_STEP_COUNT,
  type HowItWorksStepIndex,
  HowItWorksStepPlaybackContext,
  type HowItWorksStepPlaybackGenerations,
  INITIAL_HOW_IT_WORKS_STEP_PLAYBACK_GENERATIONS,
} from '@/components/support-mainnet/how-it-works/how-it-works-step-playback-api';
import { useMediaQuery } from '@/hooks/ui/use-media-query';

type StepPlaybackLockUntilMs = readonly [number, number, number];

type PlaybackOrchestrationState = {
  playbackStep: number | null;
  sectionVisible: boolean;
  hoverPlayStep: number | null;
  stepPlaybackGenerations: HowItWorksStepPlaybackGenerations;
  stepPlaybackLockedUntilMs: StepPlaybackLockUntilMs;
};

type PlaybackOrchestrationAction =
  | { type: 'intersect'; visible: boolean; startStep: boolean; nowMs: number }
  | { type: 'advance-step'; nowMs: number }
  | { type: 'reduced-motion-changed'; reducedMotion: boolean }
  | { type: 'hover-play'; stepIndex: HowItWorksStepIndex; nowMs: number }
  | { type: 'hover-play-end'; stepIndex: HowItWorksStepIndex };

const INITIAL_STEP_PLAYBACK_LOCK_UNTIL_MS: StepPlaybackLockUntilMs = [0, 0, 0];

const INITIAL_PLAYBACK_STATE: PlaybackOrchestrationState = {
  playbackStep: null,
  sectionVisible: false,
  hoverPlayStep: null,
  stepPlaybackGenerations: INITIAL_HOW_IT_WORKS_STEP_PLAYBACK_GENERATIONS,
  stepPlaybackLockedUntilMs: INITIAL_STEP_PLAYBACK_LOCK_UNTIL_MS,
};

function setStepPlaybackLockUntilMs(
  locks: StepPlaybackLockUntilMs,
  stepIndex: HowItWorksStepIndex,
  lockedUntilMs: number,
): StepPlaybackLockUntilMs {
  const next: [number, number, number] = [...locks];
  next[stepIndex] = lockedUntilMs;
  return next;
}

function lockStepPlaybackUntil(
  locks: StepPlaybackLockUntilMs,
  stepIndex: HowItWorksStepIndex,
  nowMs: number,
): StepPlaybackLockUntilMs {
  return setStepPlaybackLockUntilMs(
    locks,
    stepIndex,
    nowMs + getHowItWorksStepDurationMs(stepIndex),
  );
}

function isStepPlaybackLocked(
  locks: StepPlaybackLockUntilMs,
  stepIndex: HowItWorksStepIndex,
  nowMs: number,
): boolean {
  return locks[stepIndex] > nowMs;
}

function bumpAllStepGenerations(
  generations: HowItWorksStepPlaybackGenerations,
): HowItWorksStepPlaybackGenerations {
  return [generations[0] + 1, generations[1] + 1, generations[2] + 1];
}

function bumpStepGeneration(
  generations: HowItWorksStepPlaybackGenerations,
  stepIndex: HowItWorksStepIndex,
): HowItWorksStepPlaybackGenerations {
  const next: [number, number, number] = [...generations];
  next[stepIndex] += 1;
  return next;
}

function getPlayingStep(state: PlaybackOrchestrationState): number | null {
  if (state.hoverPlayStep !== null) {
    return state.hoverPlayStep;
  }
  if (
    state.sectionVisible &&
    state.playbackStep !== null &&
    state.playbackStep < HOW_IT_WORKS_STEP_COUNT
  ) {
    return state.playbackStep;
  }
  return null;
}

function playbackOrchestrationReducer(
  state: PlaybackOrchestrationState,
  action: PlaybackOrchestrationAction,
): PlaybackOrchestrationState {
  switch (action.type) {
    case 'intersect': {
      if (action.visible) {
        return {
          ...state,
          sectionVisible: true,
          playbackStep: action.startStep ? 0 : state.playbackStep,
          stepPlaybackLockedUntilMs: action.startStep
            ? lockStepPlaybackUntil(state.stepPlaybackLockedUntilMs, 0, action.nowMs)
            : state.stepPlaybackLockedUntilMs,
        };
      }
      return {
        playbackStep: null,
        sectionVisible: false,
        hoverPlayStep: null,
        stepPlaybackGenerations: bumpAllStepGenerations(state.stepPlaybackGenerations),
        stepPlaybackLockedUntilMs: INITIAL_STEP_PLAYBACK_LOCK_UNTIL_MS,
      };
    }
    case 'advance-step': {
      if (state.playbackStep === null) {
        return state;
      }
      const nextPlaybackStep = state.playbackStep + 1;
      if (nextPlaybackStep >= HOW_IT_WORKS_STEP_COUNT) {
        return { ...state, playbackStep: nextPlaybackStep };
      }
      return {
        ...state,
        playbackStep: nextPlaybackStep,
        stepPlaybackLockedUntilMs: lockStepPlaybackUntil(
          state.stepPlaybackLockedUntilMs,
          nextPlaybackStep as HowItWorksStepIndex,
          action.nowMs,
        ),
      };
    }
    case 'reduced-motion-changed': {
      if (!state.sectionVisible) {
        return state;
      }
      if (action.reducedMotion) {
        return { ...state, playbackStep: null, hoverPlayStep: null };
      }
      if (state.playbackStep === null) {
        return { ...state, playbackStep: 0 };
      }
      return state;
    }
    case 'hover-play': {
      if (isStepPlaybackLocked(state.stepPlaybackLockedUntilMs, action.stepIndex, action.nowMs)) {
        return state;
      }
      const playing = getPlayingStep(state);
      if (playing === action.stepIndex && state.hoverPlayStep !== action.stepIndex) {
        return state;
      }
      if (state.hoverPlayStep === action.stepIndex) {
        return state;
      }
      return {
        ...state,
        hoverPlayStep: action.stepIndex,
        stepPlaybackGenerations: bumpStepGeneration(
          state.stepPlaybackGenerations,
          action.stepIndex,
        ),
        stepPlaybackLockedUntilMs: lockStepPlaybackUntil(
          state.stepPlaybackLockedUntilMs,
          action.stepIndex,
          action.nowMs,
        ),
      };
    }
    case 'hover-play-end': {
      if (state.hoverPlayStep !== action.stepIndex) {
        return state;
      }
      return { ...state, hoverPlayStep: null };
    }
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

function useIntersectionPlayback(
  container: HTMLDivElement | null,
  dispatch: Dispatch<PlaybackOrchestrationAction>,
  reducedMotion: boolean,
): void {
  useEffect(() => {
    if (container === null) {
      return;
    }

    const emitIntersection = (visible: boolean, startStep: boolean): void => {
      dispatch({ type: 'intersect', visible, startStep, nowMs: Date.now() });
    };

    if (typeof IntersectionObserver === 'undefined') {
      emitIntersection(true, !reducedMotion);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target !== container) {
            continue;
          }

          emitIntersection(entry.isIntersecting, entry.isIntersecting && !reducedMotion);
        }
      },
      { threshold: HOW_IT_WORKS_SECTION_INTERSECTION_THRESHOLD },
    );

    observer.observe(container);

    return (): void => {
      observer.disconnect();
    };
  }, [container, dispatch, reducedMotion]);
}

export function HowItWorksStepPlaybackProvider({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [state, dispatch] = useReducer(playbackOrchestrationReducer, INITIAL_PLAYBACK_STATE);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useIntersectionPlayback(container, dispatch, reducedMotion);

  useEffect(() => {
    dispatch({ type: 'reduced-motion-changed', reducedMotion });
  }, [reducedMotion]);

  useEffect(() => {
    if (
      state.playbackStep === null ||
      state.playbackStep >= HOW_IT_WORKS_STEP_COUNT ||
      reducedMotion
    ) {
      return;
    }

    const durationMs = getHowItWorksStepDurationMs(state.playbackStep as HowItWorksStepIndex);
    const timer = window.setTimeout(() => {
      dispatch({ type: 'advance-step', nowMs: Date.now() });
    }, durationMs);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [state.playbackStep, reducedMotion]);

  useEffect(() => {
    if (state.hoverPlayStep === null || reducedMotion) {
      return;
    }

    const stepIndex = state.hoverPlayStep as HowItWorksStepIndex;
    const durationMs = getHowItWorksStepDurationMs(stepIndex);
    const timer = window.setTimeout(() => {
      dispatch({ type: 'hover-play-end', stepIndex });
    }, durationMs);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [state.hoverPlayStep, reducedMotion]);

  const activeStep =
    state.sectionVisible === false
      ? null
      : reducedMotion
        ? HOW_IT_WORKS_STEP_COUNT
        : state.playbackStep;

  const playingStep = reducedMotion ? null : getPlayingStep(state);

  function requestStepPlayback(stepIndex: HowItWorksStepIndex): void {
    if (reducedMotion) {
      return;
    }
    const nowMs = Date.now();
    if (isStepPlaybackLocked(state.stepPlaybackLockedUntilMs, stepIndex, nowMs)) {
      return;
    }
    if (playingStep === stepIndex) {
      return;
    }
    dispatch({ type: 'hover-play', stepIndex, nowMs });
  }

  return (
    <HowItWorksStepPlaybackContext
      value={{
        activeStep,
        hoverPlayStep: state.hoverPlayStep,
        playingStep,
        stepPlaybackGenerations: state.stepPlaybackGenerations,
        requestStepPlayback,
      }}
    >
      <div ref={setContainer} className={className}>
        {children}
      </div>
    </HowItWorksStepPlaybackContext>
  );
}
